import { track } from "@/lib/analytics"
import { useCallback, useEffect, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { deleteCase, getCase, getMatterContents, patchCase, removeSession, startReading } from "../api"
import type { CaseFileDetail, CaseFileRow, MatterContents } from "../contract"
import { NiraBar } from "../ds/NiraBar"
import { Rail } from "../answer/Rail"
import { lakh, shortDate } from "../answer/format"
import type { ExtraRow } from "../matter/MatterSessions"
import { NewSession, SessionGuide } from "../matter/StartHere"
import { RowMenu } from "../matter/RowMenu"
import { usePopover } from "../ds/usePopover"
import { WorkspaceDrop, type DropHandle } from "../matter/WorkspaceDrop"
import { usePageDrop } from "../matter/usePageDrop"
import { Workspace } from "./Workspace"
import { PRIVACY_URL } from "../privacy"
import { usePageTitle } from "../answer/hooks"
import "../answer/answer.css"
import "../answer/answer-v4.css"
import "../answer/pro.css"
import "./case.css"
import { caseWork } from "./work"

const day = (iso: string) => shortDate(iso.slice(0, 10))

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** "8 files · 3 sessions · Telangana · s.74 · FY 2019-20 · ₹21.6 lakh proposed". */
export function metaLine(c: CaseFileRow, files: number, sessions: number): string {
  const f = c.facts
  const years = f.periods.length ? `FY ${f.periods.map((p, i) => (i === 0 ? p : p.slice(2))).join(", ")}` : ""
  return [
    plural(files, "file"), plural(sessions, "session"),
    f.state, f.section ? `s.${f.section}` : "", years, f.proposed_total ? `${lakh(f.proposed_total)} proposed` : "",
  ].filter(Boolean).join(" · ")
}

/** The reply-by date as a pill, in the caution colour when it is under 14 days away. */
function DuePill({ due }: { due: string }) {
  const soon = Date.parse(due) - Date.now() < 14 * 864e5
  return <span className={`ncf-due${soon ? " soon" : ""}`}>{`Reply by ${shortDate(due)}`}</span>
}

const hasNoticeKind = (data: CaseFileDetail) => data.documents.some((d) => d.kind === "notice")

/**
 * The workspace page (NA-CaseFile), progressive in three stages:
 *  A. no files and no sessions: the drop zone is the page;
 *  B. files, no sessions: the files and a guide to starting a session;
 *  C. sessions (or readings): the sessions list, with the files beside it.
 */
export default function CasePage() {
  const { caseId = "" } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<CaseFileDetail | null>(null)
  // What the Matter holds: its sessions of every type and its file shelf. Null until it answers or if it cannot.
  const [contents, setContents] = useState<MatterContents | null>(null)
  const [contentsFailed, setContentsFailed] = useState(false)
  const [renaming, setRenaming] = useState<string | null>(null)
  const titleRow = useRef<HTMLDivElement>(null)
  const drop = useRef<DropHandle>(null)
  // The whole page takes a drop of files once the workspace has any; the overlay says where they go.
  // The tab lives in the URL (?tab=files). A file dropped anywhere switches to Files, then uploads once its card is there.
  const [params, setParams] = useSearchParams()
  const pending = useRef<File[] | null>(null)
  const dragging = usePageDrop((files) => {
    if (drop.current) { drop.current.take(files, "drop"); return }
    pending.current = files
    setParams((p) => { const n = new URLSearchParams(p); n.set("tab", "files"); return n }, { replace: true })
  })
  useEffect(() => {
    if (pending.current && drop.current) { drop.current.take(pending.current, "drop"); pending.current = null }
  })
  const wasRenaming = useRef(false)
  // Save or Cancel hands focus back to the Rename button the reader came from.
  useEffect(() => {
    if (renaming === null && wasRenaming.current) titleRow.current?.querySelector<HTMLButtonElement>(".rm-btn")?.focus()
    wasRenaming.current = renaming !== null
  }, [renaming])
  const [failed, setFailed] = useState("")
  const [histOpen, setHistOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState("")
  // A note handed over by the page that sent the reader here (the confirm step's save), or word
  // that the notice they dropped was already here (a second drop of the same document).
  const sent = useLocation().state as { said?: string; again?: boolean } | null
  const handed = sent?.said ?? ""
  const again = !!sent?.again

  const opened = useRef<string | null>(null)
  const load = useCallback(() => {
    setFailed("")
    getCase(caseId)
      .then((next) => {
        // Folded into an earlier case file for the same document: that one is the case file.
        if (next.merged_into) navigate(`/case/${encodeURIComponent(next.merged_into)}`, { replace: true, state: { again: true } })
        else {
          setData(next)
          if (opened.current !== next.id) {
            opened.current = next.id
            track("matter_opened")
          }
        }
      })
      .catch((err: { status?: number }) => setFailed(err?.status === 404 ? "gone" : "error"))
  }, [caseId, navigate])
  useEffect(load, [load])
  const loadContents = useCallback(() => {
    let live = true
    // A sim that cannot answer is listed under `unavailable`; if Nira itself cannot, the page opens without them.
    getMatterContents(caseId)
      .then((next) => { if (live) { setContents(next); setContentsFailed(false) } })
      .catch(() => { if (live) setContentsFailed(true) })
    return () => { live = false }
  }, [caseId])
  useEffect(loadContents, [loadContents])
  usePageTitle(data?.title)

  const rename = async () => {
    if (!data || renaming === null) return
    const title = renaming.trim()
    if (!title || title === data.title) { setRenaming(null); return }
    try {
      const next = await patchCase(data.id, { title })
      setData({ ...data, title: next?.title ?? title })
      setRenaming(null)
    } catch {
      setSaid("Couldn't save the name. Try again.")
    }
  }

  const toggle = async (key: "keep_after_close") => {
    if (!data) return
    const next = !data[key]
    setData({ ...data, [key]: next })
    try {
      await patchCase(data.id, { [key]: next })
    } catch {
      setData({ ...data, [key]: !next })
      setSaid("Couldn't save that. Try again.")
    }
  }

  // The same document dropped again: read it again here, on the same credit.
  const rerun = async (kind: "notice_no_reply" | "order_preview") => {
    if (!data) return
    setBusy(true)
    try {
      const reading = await startReading(data.id, kind)
      navigate(`/case/${encodeURIComponent(data.id)}/reading/${encodeURIComponent(reading.id)}`)
    } catch {
      setBusy(false)
      setSaid("Couldn't start the reading. Try again.")
    }
  }

  const remove = async () => {
    if (!data) return
    setBusy(true)
    try {
      // Order drafts are unlinked; Nira threads are soft-deleted by the delete itself. A draft
      // that cannot be unlinked never blocks the delete; it is left pointing at a Matter that is gone.
      const drafts = (contents?.sessions ?? []).filter((x) => x.type !== "nira")
      await Promise.all(drafts.map((d) => removeSession(data.id, d.type, d.id).catch(() => {})))
      await deleteCase(data.id)
      navigate("/workspaces", { replace: true })
    } catch (err) {
      setBusy(false)
      setConfirming(false)
      setSaid((err as { status?: number })?.status === 409
        ? "An answer is still running in this workspace. Try again once it finishes."
        : "Couldn't delete the workspace. Try again.")
    }
  }

  const bar = (
    <>
      <NiraBar onMenu={() => setHistOpen((v) => !v)} menuOpen={histOpen} menuControls="nira-hlist" crumbs={[{ label: "Workspaces", to: "/workspaces" }, { label: data ? data.title : "Workspace" }]} />
      <Rail threadId="" histOpen={histOpen} onHist={setHistOpen} refreshKey={0} drawerOnly caseId={caseId} scope={data?.documents.some((d) => d.kind === "order") ? "appeal" : "notice"} />
      <div className={`scrim${histOpen ? " on" : ""}`} aria-hidden="true" onClick={() => setHistOpen(false)} />
    </>
  )

  if (!data) {
    return (
      <div className="na2 na4 ncf">
        {bar}
        <main className="ncf-main">
          {failed === "gone" ? (
            <div className="ncf-msg"><h1>This workspace does not exist, or was deleted.</h1><p><Link to="/ask">Back to Nira</Link></p></div>
          ) : failed ? (
            <div className="ncf-msg"><h1>The workspace could not be opened.</h1><p><button type="button" className="pro-link" onClick={load}>Try again</button></p></div>
          ) : (
            <p className="ncf-msg" role="status">Opening the workspace…</p>
          )}
        </main>
      </div>
    )
  }

  const hasNotice = data.documents.some((d) => d.kind === "notice" || d.kind === "order")
  const hasOrder = data.documents.some((d) => d.kind === "order")
  // Readings, newest first. Searches and drafts are the Matter's sessions, listed below from its contents.
  const work = caseWork({ ...data, threads: [] })
  const sessionsTotal = contents?.sessions_total ?? 0
  const extra: ExtraRow[] = work.map((w) => ({ key: w.key, to: w.to, title: w.question ? `${w.what}: “${w.question}”` : w.what, feature: w.feature, at: w.at, line: w.line, docIds: w.docIds }))
  const loaded = contents !== null
  const fileCount = Math.max(contents?.files.length ?? 0, data.documents.length)
  const sessionCount = sessionsTotal + extra.length
  // Stage C once anything has been started here; B once files are in; else A.
  const stage: "A" | "B" | "C" = sessionsTotal + (contents?.sessions.length ?? 0) + work.length > 0 ? "C" : fileCount > 0 ? "B" : "A"
  const open = data.asks.filter((a) => a.status === "open")
  const menu = [
    { key: "rename", label: "Rename", onSelect: () => setRenaming(data.title) },
    { key: "keep", label: "Keep after it closes", checked: data.keep_after_close, onSelect: () => void toggle("keep_after_close") },
    { key: "delete", label: "Delete workspace", danger: true, onSelect: () => setConfirming(true) },
  ]
  const meta = metaLine(data, fileCount, sessionCount)
  const tabParam = params.get("tab")
  const tab: "sessions" | "files" = tabParam === "files" ? "files" : "sessions"
  const setTab = (t: "sessions" | "files") => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", t); return n }, { replace: true })

  return (
    <div className="na2 na4 ncf">
      {bar}
      <main className="ncf-main">
        <header className="ncf-head">
          <div className="ncf-title">
            {renaming === null ? (
              <div ref={titleRow} className="ncf-titlerow">
                <h1>{data.title}</h1>
                <RowMenu label="Workspace options" items={menu} align="left" className="ncf-opts" />
              </div>
            ) : (
              <form className="ncf-renameform" onSubmit={(e) => { e.preventDefault(); void rename() }}>
                <input aria-label="Workspace name" value={renaming} autoFocus onChange={(e) => setRenaming(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") setRenaming(null) }} />
                <button type="submit" className="ncf-btn primary">Save</button>
                <button type="button" className="ncf-btn" onClick={() => setRenaming(null)}>Cancel</button>
              </form>
            )}
            {data.facts.doc_title && data.facts.doc_title !== data.title ? <p className="ncf-doc-title">{data.facts.doc_title}</p> : null}
            <p className="ncf-meta"><span>{meta}</span>{data.facts.reply_due ? <DuePill due={data.facts.reply_due} /> : null}</p>
          </div>
          <div className="ncf-acts">
            <NewSession caseId={data.id} title={data.title} align="right" proceedings={contents?.proceedings} files={contents?.files} />
          </div>
        </header>
        {confirming ? (
          <div className="ncf-confirm" role="group" aria-label="Delete workspace">
            <p>
              Delete {data.title}? Its files and {sessionsTotal} {sessionsTotal === 1 ? "session" : "sessions"} are removed from your account at once, and copies are erased from our systems within 30 days. This can't be undone.
            </p>
            <div>
              <button type="button" className="ncf-btn danger" disabled={busy} onClick={() => void remove()}>{busy ? "Deleting…" : "Delete everything"}</button>
              <button type="button" className="ncf-btn" disabled={busy} onClick={() => setConfirming(false)}>Keep it</button>
            </div>
          </div>
        ) : null}
        {again ? (
          <div className="ncf-again" role="status">
            <span>You dropped this {hasOrder && !hasNoticeKind(data) ? "order" : "notice"} before, so it opened its workspace. Every reading of it is here.</span>
            <button type="button" className="ncf-btn primary" disabled={busy} onClick={() => void rerun(hasOrder && !hasNoticeKind(data) ? "order_preview" : "notice_no_reply")}>
              Re-run the reading
            </button>
          </div>
        ) : null}
        {said ? <p className="ncf-said" role="status">{said}</p> : handed ? <p className="ncf-said ok" role="status">{handed}</p> : null}
        {contentsFailed ? (
          <p className="ncf-said" role="status">Couldn't load this workspace's sessions and files. <button type="button" className="pro-link" onClick={loadContents}>Try again</button></p>
        ) : !loaded ? (
          <p className="ncf-msg ncf-wait" role="status">Opening the workspace…</p>
        ) : null}

        {loaded || contentsFailed ? (
          <div className="ncf-col ncf-stack">
            <Tabs tab={tab} onTab={setTab} sessions={sessionCount} files={fileCount} />
            {stage === "A" ? (
              <div role="tabpanel" id={`wsx-panel-${tab}`} aria-labelledby={`wsx-tab-${tab}`}>
                {tab === "files" ? <WorkspaceDrop ref={drop} caseId={data.id} variant="hero" onChanged={loadContents} />
                  : <SessionGuide caseId={data.id} title={data.title} proceedings={contents?.proceedings} files={contents?.files} showAction={false} />}
              </div>
            ) : (
              loaded && contents ? <Workspace caseId={data.id} title={data.title} contents={contents} onChanged={loadContents} tab={tab} dropRef={drop} dragging={dragging}
                sessions={stage === "C" ? contents.sessions : []} extra={stage === "C" ? extra : []} /> : null
            )}
              {open.length ? (
                <div className="ncf-asks">
                  <span>Still asked for:</span>
                  {open.map((a) => <span className="ncf-ask" key={a.id}>+ {a.what || a.document_kind}{a.allegation ? `, ${a.allegation}` : ""}</span>)}
                </div>
              ) : null}
          </div>
        ) : null}

        {dragging ? <div className="wsx-over" aria-hidden="true"><p>{`Drop to add to ${data.title}`}</p></div> : null}
        <Privacy />
      </main>
    </div>
  )
}

/** Sessions | Files: a tablist the arrow keys move along; the count is a muted number. */
function Tabs({ tab, onTab, sessions, files }: { tab: "sessions" | "files"; onTab: (t: "sessions" | "files") => void; sessions: number; files: number }) {
  const items = [{ id: "sessions" as const, label: "Sessions", n: sessions }, { id: "files" as const, label: "Files", n: files }]
  const key = (e: React.KeyboardEvent) => {
    const at = items.findIndex((i) => i.id === tab)
    const to = e.key === "ArrowRight" ? (at + 1) % 2 : e.key === "ArrowLeft" ? (at + 1) % 2 : e.key === "Home" ? 0 : e.key === "End" ? 1 : -1
    if (to < 0) return
    e.preventDefault()
    onTab(items[to].id)
    document.getElementById(`wsx-tab-${items[to].id}`)?.focus()
  }
  return (
    <div className="wsx-tabs" role="tablist" aria-label="Workspace" onKeyDown={key}>
      {items.map((i) => (
        <button key={i.id} id={`wsx-tab-${i.id}`} type="button" role="tab" aria-selected={tab === i.id} aria-controls={`wsx-panel-${i.id}`}
          tabIndex={tab === i.id ? 0 : -1} className="wsx-tab" onClick={() => onTab(i.id)}>
          {i.label}<span className="wsx-tabn">{i.n}</span>
        </button>
      ))}
    </div>
  )
}

/** One muted line about who sees the workspace; "Details" opens the full text. */
function Privacy() {
  const { open, setOpen, button, panel } = usePopover<HTMLButtonElement, HTMLDivElement>()
  return (
    <footer className="ncf-foot">
      <p>
        Other users cannot see this workspace · Never used to train a model ·{" "}
        <button ref={button} type="button" className="ncf-foot-b" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((v) => !v)}>Details</button>
      </p>
      {open ? (
        <div ref={panel} role="dialog" aria-label="What's kept, who can see it" className="ncf-foot-pop">
          <p><b>Kept:</b> the files you add, the facts Nira reads from them, every session and the dates.</p>
          <p><b>Never used to train a model.</b> Used only for your sessions in this workspace.</p>
          <p>
            <b>Who can see it:</b> you, and anyone you forward a reading to, for that reading only.{" "}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer">What Nira does with your documents</a>
          </p>
        </div>
      ) : null}
    </footer>
  )
}

/** The list page lives in WorkspacesPage.tsx; re-exported for the old import path. */
export { CasesPage } from "./WorkspacesPage"
